import { Button, Group, Tabs } from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconDeviceFloppy,
  IconFileText,
  IconLayoutDashboard,
  IconPalette,
  IconTrash,
} from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  CHROME_CSS_STYLE_KEYS,
  DEFAULT_CHROME_CSS_STYLES,
  EMPTY_CHROME_CSS_STYLES,
  MODAL_DISPLAY_TYPES,
  cardControlsOffered,
  cardOffsetError,
  cardSizeError,
  pruneUnreachableCardCssStyles,
} from '../../constants/bannerChrome';
import { parseBannerDesign, skippedAppContentNote } from '../../helpers/appDesignMarkup';
import { MOBILE_VIEWPORT_HEIGHT, SHOP_VIEWPORT_HEIGHT } from '../../helpers/previewFit';
import useAllowedActions from '../../helpers/useAllowedActions';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import PageBlockAppearance from '../PageBlockAppearance';
import PageBlockDisplayConfiguration from '../PageBlockDisplayConfiguration';
import PositionOnMySite from '../PositionOnMySite';
import SdkPreview from '../SdkPreview';
import BannerBlockCopyModal from './BannerBlockCopyModal';
import ContentBannerBlockForm from './ContentBannerBlockForm';
import previewBannerPayload from './previewBannerPayload';

const tabs = ['content', 'displayConfiguration', 'appearance'];

const BannerBlockForm = ({ type = 'bannerBlocks', bannerBlock: passedBannerBlock, cancel, onSaved }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const search = queryString.parse(location.search);
  const tab = search.tab || tabs[0];
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [banners, setBanners] = useState();
  const [assets, setAssets] = useState();
  const [segments, setSegments] = useState();
  const [bannerBlock, setBannerBlock] = useState(passedBannerBlock);
  const [userError, setUserError] = useState();
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState();
  const [customFields, setCustomFields] = useState();
  const [templates, setTemplates] = useState();
  const [productFeeds, setProductFeeds] = useState();
  const domain = useSelector((state) => state.domain.domain);
  const allowedActions = useAllowedActions(domain);
  const allowedActionsStringified = JSON.stringify([...(allowedActions || [])]);

  const setTab = (tab) => {
    navigate(`${location.pathname}?${queryString.stringify({ ...search, tab })}`);
  };

  const refreshBanners = useCallback(
    (abortController, cb) => {
      const options = {};

      if (abortController) {
        options.signal = abortController.signal;
      }

      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/banners`, options)
        .then(({ banners }) => {
          const allowedActions = JSON.parse(allowedActionsStringified);
          setBanners(
            banners.filter(
              (b) =>
                !allowedActions || allowedActions.includes('postPurchaseBannersAttach') || b.scope !== 'postPurchase'
            )
          );

          if (typeof cb === 'function') {
            cb();
          }
        })
        .catch(setUserError);
    },
    [accountId, domainId, allowedActionsStringified, fetch]
  );

  useEffect(() => {
    if (Object.keys(bannerBlock.cssStyles || {}).length > 0) {
      return;
    }

    // A block with no stored `cssStyles` is seeded with the chrome its display
    // mode draws: the defaults for the modes the SDK wraps in a card, the blank
    // shape for the ones it does not.
    const updatedCssStyles = MODAL_DISPLAY_TYPES.includes(bannerBlock.displayType)
      ? DEFAULT_CHROME_CSS_STYLES
      : EMPTY_CHROME_CSS_STYLES;

    setBannerBlock({ ...bannerBlock, cssStyles: { ...updatedCssStyles } });
  }, [bannerBlock]);

  useEffect(() => {
    const abortController = new AbortController();

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/templates`, { signal: abortController.signal })
      .then(({ templates }) => setTemplates(templates.filter((t) => t.scope === 'web')))
      .catch(setUserError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/productFeeds`, { signal: abortController.signal })
      .then(({ productFeeds }) => setProductFeeds(productFeeds.filter((f) => f.scope === 'web')))
      .catch(setUserError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments?scope=internal`, {
      signal: abortController.signal,
    })
      .then(({ segments }) => setSegments(segments))
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/assets`, { signal: abortController.signal })
      .then(({ assets }) => setAssets(assets.map((a) => a.url)))
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`, { signal: abortController.signal })
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setLoadError);

    refreshBanners(abortController);

    return () => {
      abortController.abort();
    };
  }, [accountId, domainId, allowedActionsStringified, refreshBanners, fetch]);

  if (
    templates === undefined ||
    productFeeds === undefined ||
    assets === undefined ||
    segments === undefined ||
    customFields === undefined
  ) {
    return '';
  }

  // Updated from the previous state rather than from the `bannerBlock` this
  // render closed over, because one gesture can now produce two calls: the
  // advanced-styling panel's card position controls write a `cssStyles` key
  // and mirror it onto `displayPosition`. Read from the closure, the second
  // call would be computed from the pre-gesture block and drop the first.
  const handleStateChange = (e) => {
    const { name, value } = e.target;
    setBannerBlock((previous) => {
      const change = {};
      // A chrome field names the `cssStyles` key it edits, not a column on the block.
      if (CHROME_CSS_STYLE_KEYS.has(name)) {
        change.cssStyles = { ...previous.cssStyles, [name]: value };
        // And it turns `advancedStyling` on, because that flag is what decides
        // whether the author's styling reaches the shop at all: the API serves
        // `advancedStyling === false` the documented defaults and throws the
        // stored `cssStyles` away (`helpers/resolve-popup-css-styles.js`). It
        // used to be a switch the author flipped before the panel appeared;
        // with the panel always on the Appearance tab, nothing would ever turn
        // it on, and every edit here would show in the preview — which renders
        // from this form's state — and nowhere else. Derived from the act of
        // styling rather than asked for separately.
        change.advancedStyling = true;
      } else if (name === 'urlFilter' && value === false) {
        change.urlRegex = '';
        change.urlRegexNoMatch = '';
      } else {
        change[name] = value;
      }
      if (name === 'displayType' && value === 'static') {
        change.trigger = 'immediately';
      }
      return { ...previous, ...change };
    });
  };

  const validateBannerBlock = (bannerBlock) => {
    if (!bannerBlock.name) {
      return false;
    }

    if (bannerBlock.displayType === 'popup' && !bannerBlock.trigger) {
      return false;
    }

    if (bannerBlock.displayType === 'static' && !bannerBlock.cssSelector) {
      return false;
    }

    // The size/offset fields render their own inline error, but that is
    // advisory unless something also refuses to save on it — otherwise the
    // author can click Save on a red field and get the API's 400 instead.
    // Scoped to the state that actually offers the control it came from
    // (`PageBlockAdvancedStyling` only mounts with the switch on for a modal
    // display type, and hides the card-width/offset-horizontal row for
    // `bar`): a stale value left over from before the switch was turned off,
    // or from before the display type changed to `bar`, has no control left
    // to fix it through, so it must not veto a save it cannot explain.
    const cardControlsOnScreen = cardControlsOffered(bannerBlock.displayType);
    if (
      (cardControlsOnScreen && bannerBlock.displayType !== 'bar' && cardSizeError(bannerBlock.cssStyles?.cardWidth)) ||
      (cardControlsOnScreen && cardSizeError(bannerBlock.cssStyles?.cardHeight)) ||
      (cardControlsOnScreen &&
        bannerBlock.displayType !== 'bar' &&
        cardOffsetError(bannerBlock.cssStyles?.cardOffsetHorizontal)) ||
      (cardControlsOnScreen && cardOffsetError(bannerBlock.cssStyles?.cardOffsetVertical))
    ) {
      return false;
    }

    return true;
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  const adjustBannerBlockProperties = (bannerBlock, trigger) => {
    return {
      ...bannerBlock,
      delaySeconds: trigger === 'delaySeconds' ? bannerBlock.delaySeconds : null,
      scrollPercentage: trigger === 'scrollPercentage' ? bannerBlock.scrollPercentage : null,
    };
  };

  const pagesEndpoint = `/api/admin/accounts/${accountId}/domains/${domainId}/pages`;

  const saveBannerBlock = (passedBannerBlock, { close = true } = {}) => {
    const isUpdate = !passedBannerBlock && bannerBlock.id;
    if (!passedBannerBlock) {
      passedBannerBlock = bannerBlock;
    }

    const shouldAdjustProperties = passedBannerBlock.displayType === 'popup' && passedBannerBlock.trigger;
    const triggerAdjustedBannerBlock = shouldAdjustProperties
      ? adjustBannerBlockProperties(passedBannerBlock, passedBannerBlock.trigger)
      : passedBannerBlock;
    // A `cardWidth`/`cardHeight` typed while its control was on screen
    // outlives the control: `validateBannerBlock` above already stops
    // gating Save on one the author can no longer see, so without this the
    // stale value would still ride along in the payload and the API would
    // 400 on it with nothing on the form to point back to.
    const baseBannerBlock = {
      ...triggerAdjustedBannerBlock,
      cssStyles: pruneUnreachableCardCssStyles(triggerAdjustedBannerBlock.cssStyles, triggerAdjustedBannerBlock),
    };

    fetch(`${pagesEndpoint}/${passedBannerBlock.pageId}/bannerBlocks${isUpdate ? '/' + bannerBlock.id : ''}`, {
      method: isUpdate ? 'PUT' : 'POST',
      body: JSON.stringify({ bannerBlock: baseBannerBlock }),
    })
      .then(({ bannerBlock: savedBannerBlock }) => {
        // Reported where the write lands, so the page can retire a positioning
        // session whose stored snapshot this save has just superseded (see
        // `useRestorePlacementSession`). Before the navigation, which is a
        // push: the page strips the session parameter with a `replace`, which
        // has to land on this entry rather than on the one pushed next.
        // Updates only — the copy modal's `POST` creates a different block, so
        // it supersedes nothing.
        if (isUpdate) {
          onSaved?.();
        }
        if (close) {
          navigate(`/accounts/${accountId}/domains/${domainId}/pages?type=${type}&page=${passedBannerBlock.pageId}`);
          return;
        }
        setBannerBlock((previous) => ({ ...previous, ...savedBannerBlock }));
        setSaving(false);
        setUserError(undefined);
      })
      .catch((err) => {
        setUserError(err);
        setSaving(false);
      });
  };

  const save = () => {
    setSaving(true);
    saveBannerBlock(undefined, { close: false });
  };

  const deleteBannerBlock = () => {
    fetch(`${pagesEndpoint}/${bannerBlock.pageId}/bannerBlocks/${bannerBlock.id}`, {
      method: 'DELETE',
    })
      .then(() => cancel())
      .catch(setLoadError);
  };

  const getDate = (key) => {
    const date = bannerBlock[key];
    if (!date) {
      return;
    }
    return new Date(date);
  };

  if (bannerBlock === undefined || banners === undefined) {
    return '';
  }

  const isValid = validateBannerBlock(bannerBlock);

  const saveButtons = (
    <>
      {bannerBlock.id && (
        <LockableButton
          restrictionKey="bannerBlocksUpdate"
          variant="light"
          disabled={saving || !isValid}
          loading={saving}
          onClick={save}
        >
          <IconDeviceFloppy size={14} /> Save
        </LockableButton>
      )}
      <LockableButton
        restrictionKey={`bannerBlocks${bannerBlock.id ? 'Update' : 'Create'}`}
        disabled={saving || !isValid}
        variant="primary"
        onClick={() => saveBannerBlock()}
        data-tour="bannerBlockSaveBtn"
      >
        <IconDeviceFloppy size={14} /> Save & Close
      </LockableButton>
    </>
  );

  const nextButton = (
    <Button
      onClick={() => {
        setTab(tabs[tabs.indexOf(tab) + 1]);
        window.scrollTo(0, 0);
      }}
    >
      Next <IconChevronRight size={14} />
    </Button>
  );

  // A new block used to be saveable only from the last tab, which was the
  // display configuration. Appearance now sits after it and is optional — a
  // block with no chrome styled saves perfectly well — so Save is offered from
  // the display configuration on, with Next still there for an author who does
  // want to style it. A block that already exists keeps Save on every tab.
  const saveOffered = !!bannerBlock.id || tabs.indexOf(tab) >= tabs.indexOf('displayConfiguration');
  const nextOffered = !bannerBlock.id && tabs.indexOf(tab) < tabs.length - 1;

  const banner = banners.find((b) => b.id === bannerBlock.bannerId);
  // The Unlayer design, which is what the native app SDKs render — the web SDK
  // renders `banner.html` and never looks at it.
  const bannerDesign = parseBannerDesign(banner?.design);
  const previewBanner = previewBannerPayload(banner, bannerBlock);

  return (
    <>
      <form>
        <Tabs value={tab} onChange={setTab} mb="md" keepMounted>
          <Tabs.List>
            <Tabs.Tab value="content" leftSection={<IconFileText size={16} />}>
              Content
            </Tabs.Tab>
            <Tabs.Tab
              value="displayConfiguration"
              leftSection={<IconLayoutDashboard size={16} />}
              data-tour="bannerBlockDisplayConfigurationTab"
            >
              Display Configuration
            </Tabs.Tab>
            <Tabs.Tab value="appearance" leftSection={<IconPalette size={16} />}>
              Appearance
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="content" pt={0}>
            <ContentBannerBlockForm
              active={tab === 'content'}
              banners={banners}
              bannerBlock={bannerBlock}
              handleStateChange={handleStateChange}
              setUserError={setUserError}
              refreshBanners={refreshBanners}
            />
          </Tabs.Panel>
          <Tabs.Panel value="displayConfiguration" pt={0}>
            {tab === 'displayConfiguration' && (
              <PageBlockDisplayConfiguration
                pageBlock={bannerBlock}
                handleChange={handleStateChange}
                getDate={getDate}
                withLeaveIntentBackButton
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="appearance" pt={0}>
            {tab === 'appearance' && (
              <PageBlockAppearance
                // The selector row moved to this tab with the rest of "where
                // the block goes"; it is only rendered for a static block.
                positionOnSite={(cssSelectorField) => (
                  <PositionOnMySite block={bannerBlock} blocksPath="banner-blocks" blockLabel="banner block">
                    {cssSelectorField}
                  </PositionOnMySite>
                )}
                pageBlock={bannerBlock}
                handleChange={handleStateChange}
                // A function of the device, so switching to Mobile re-renders
                // the block in a phone-shaped viewport rather than putting a
                // bezel around the desktop one. A modal display type is sized
                // by the viewport and contributes no height of its own, so it
                // is the one that needs a stand-in height — and which stand-in
                // depends on the device.
                renderPreview={(device) => {
                  // `app` is a phone too, and for a `static` or `custom`
                  // block it is the same render: those are inserted into the
                  // screen on every platform, and only the card the SDKs
                  // draw around the other three differs between web and app.
                  const phone = device !== 'desktop';
                  const appChrome = device === 'app' && MODAL_DISPLAY_TYPES.includes(bannerBlock.displayType);

                  return (
                    <SdkPreview
                      method="renderBanner"
                      // The design is what the app render is built from: its
                      // rows are the content, and its `popupWidth`,
                      // `borderRadius`, `popupBackgroundColor`,
                      // `backgroundImage` and `popupOverlay_backgroundColor`
                      // are what the app SDKs fall back to for a card the
                      // block did not style. Attached on every device, so
                      // switching between Mobile and App keeps the payload key
                      // and the web SDK does not run a second time.
                      payload={
                        MODAL_DISPLAY_TYPES.includes(bannerBlock.displayType)
                          ? { ...previewBanner, design: bannerDesign }
                          : previewBanner
                      }
                      appChrome={appChrome}
                      baseUrl={domain?.url}
                      viewport={phone ? 'mobile' : 'desktop'}
                      withBorder={false}
                      // A phone is always phone-shaped: a `static` block
                      // measures its own content, and a short one drew a 200px
                      // tall iPhone. On the desktop only a modal display type
                      // needs a stand-in, since it is `position: fixed` and
                      // contributes no height of its own.
                      viewportHeight={
                        phone
                          ? MOBILE_VIEWPORT_HEIGHT
                          : MODAL_DISPLAY_TYPES.includes(bannerBlock.displayType)
                            ? SHOP_VIEWPORT_HEIGHT
                            : undefined
                      }
                    />
                  );
                }}
                previewUrl={domain?.url}
                appContentNote={skippedAppContentNote(bannerDesign)}
                // The SDK draws the modal, its overlay and its close button
                // inside the frame now, so the form's own approximation of them
                // around it would be a second, differently-styled copy.
                withChrome={false}
                // `displayPosition` is a column on `bannerBlocks`, so the
                // panel's position controls can mirror onto it and the legacy
                // Position control has something to hand an axis back to.
                displayPositionPersisted
              />
            )}
          </Tabs.Panel>
        </Tabs>
        <Group justify="flex-end" gap="xs" py="md">
          {bannerBlock.id || tabs.indexOf(tab) === 0 ? (
            <Button variant="default" onClick={() => cancel()}>
              <IconChevronLeft /> Back
            </Button>
          ) : (
            <Button variant="default" onClick={() => setTab(tabs[tabs.indexOf(tab) - 1])}>
              <IconChevronLeft /> Back
            </Button>
          )}

          {bannerBlock.id && (
            <>
              <LockableButton
                restrictionKey="bannerBlocksDelete"
                color="red"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                <IconTrash size={14} /> Delete
              </LockableButton>
              <LockableButton restrictionKey="bannerBlocksCreate" color="cyan" onClick={() => setCopyModalOpen(true)}>
                <IconCopy size={14} /> Make a copy
              </LockableButton>
            </>
          )}

          {nextOffered && nextButton}
          {saveOffered && saveButtons}
        </Group>
      </form>
      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={deleteBannerBlock}
        message="This action cannot be undone!"
      />
      <BannerBlockCopyModal
        bannerBlock={{ ...bannerBlock, id: undefined, token: undefined, pageId: undefined, running: false }}
        show={copyModalOpen}
        save={(bannerBlock) => saveBannerBlock(bannerBlock)}
        close={() => setCopyModalOpen(false)}
      />
      {userError && (
        <div>
          <ErrorMessage message={userError.message} />
        </div>
      )}
    </>
  );
};

export default BannerBlockForm;
